// The board's fixed vocabulary: states, priorities, an issue's parts and
// their budgets, the kinds of record entry, how an actor is named, and what
// a prompt's words must be. The names are today's pages', and the model's
// where today's pages have none.
import type { Actor, ColumnType, EntryKind, Issue, Part, PlanStatus, Priority, Project, ProjectColumn, Prompt, StateSlug } from './api/types'

export const STATES: readonly { slug: StateSlug; name: string }[] = [
  { slug: 'backlog', name: 'Backlog' },
  { slug: 'todo', name: 'Todo' },
  { slug: 'in_progress', name: 'In Progress' },
  { slug: 'on_main', name: 'On Main' },
  { slug: 'done', name: 'Done' },
  { slug: 'cancelled', name: 'Cancelled' },
  { slug: 'duplicate', name: 'Duplicate' },
]

/** The board's columns, in order; Cancelled and Duplicate show only when asked. */
export const BOARD_STATES: readonly StateSlug[] = ['backlog', 'todo', 'in_progress', 'on_main', 'done']
export const CLOSED_STATES: readonly StateSlug[] = ['cancelled', 'duplicate']

export function stateName(slug: StateSlug): string {
  return STATES.find((s) => s.slug === slug)?.name ?? slug
}

/** The most columns of its own a project adds, and the longest a name: the model's (column.rs). */
export const COLUMN_LIMIT = 12
export const COLUMN_NAME_MAX = 40

/** The types a column is made with, in the order a menu lists them, and the state each maps to. No type maps to On Main: only Poulpe sets that. */
export const COLUMN_TYPES: readonly { type: ColumnType; name: string; state: StateSlug }[] = [
  { type: 'backlog', name: 'Backlog', state: 'backlog' },
  { type: 'unstarted', name: 'Not started', state: 'todo' },
  { type: 'started', name: 'Started', state: 'in_progress' },
  { type: 'completed', name: 'Done', state: 'done' },
  { type: 'canceled', name: 'Cancelled', state: 'cancelled' },
  { type: 'duplicate', name: 'Duplicate', state: 'duplicate' },
]

export function columnState(column: Pick<ProjectColumn, 'type'>): StateSlug {
  return COLUMN_TYPES.find((t) => t.type === column.type)?.state ?? 'backlog'
}

/** A project's columns of its own, none for a project from an older server. */
export function ownColumns(project: Pick<Project, 'columns'> | undefined): readonly ProjectColumn[] {
  return project?.columns ?? []
}

/** The column an issue stands in: its own column's id when its project has it, else its state. The board's column key. */
export function placeOf(issue: Pick<Issue, 'state' | 'column'>, columns: readonly ProjectColumn[]): string {
  return issue.column && columns.some((c) => c.id === issue.column) ? issue.column : issue.state
}

/** Where an issue stands, in words: its column's name, else its state's. */
export function placeName(issue: Pick<Issue, 'state' | 'column'>, columns: readonly ProjectColumn[]): string {
  const own = issue.column ? columns.find((c) => c.id === issue.column) : undefined
  return own ? own.name : stateName(issue.state)
}

const PRIORITY_NAMES: Record<Priority, string> = { none: 'No priority', urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' }

/** Urgent first, "no priority" last: the order a menu and a sort use. */
export const PRIORITY_ORDER: readonly Priority[] = ['urgent', 'high', 'medium', 'low', 'none']

export function priorityName(priority: Priority): string {
  return PRIORITY_NAMES[priority] ?? PRIORITY_NAMES.none
}

/** Rank for sorting: urgent 0 … low 3, none 4. */
export function priorityRank(priority: Priority): number {
  const rank = PRIORITY_ORDER.indexOf(priority)
  return rank < 0 ? PRIORITY_ORDER.length - 1 : rank
}

export const PARTS: readonly Part[] = ['description', 'plan', 'outcome']

export const PART_NAMES: Record<Part, string> = { description: 'Description', plan: 'Plan', outcome: 'Outcome' }

/** A part's text: the plan's is its text, whatever its approval. */
export function partText(issue: Issue, part: Part): string {
  return part === 'plan' ? issue.plan.text : issue[part]
}

/** How long each part may be, in characters, before it is flagged; it is never refused. The model's budgets (issue.rs). */
export const PART_BUDGETS: Record<Part, number> = { description: 800, plan: 1_200, outcome: 1_000 }

/** A part's length as the budget counts it: in characters, not UTF-16 units. */
export function partLength(text: string): number {
  return [...text].length
}

/** The parts of an issue longer than their budget, as the server flags them. */
export function overBudget(issue: Issue): Part[] {
  return PARTS.filter((part) => partLength(partText(issue, part)) > PART_BUDGETS[part])
}

export const RECORD_KIND_NAMES: Record<EntryKind, string> = {
  note: 'Note',
  decision: 'Decision',
  discussion: 'Discussion',
  session: 'Session',
  review: 'Review',
  question: 'Question',
  answer: 'Answer',
  landing: 'Landing',
  evidence: 'Evidence',
  handoff: 'Handoff',
}

/** The agents' names by their model's maker, as today's pages name them. */
const AGENT_NAMES_BY_VENDOR: Readonly<Record<string, string>> = { anthropic: 'Claude', openai: 'Codex' }

/**
 * How an actor is named: an agent as today's pages name it (Claude, Codex;
 * another by its model), Poulpe as itself. The server names no person yet,
 * only their membership, so a person is "Person" (sessions/words.ts,
 * senderName, names the person signed in).
 */
export function actorName(actor: Actor): string {
  switch (actor.kind) {
    case 'person':
      return 'Person'
    case 'agent':
      return Object.hasOwn(AGENT_NAMES_BY_VENDOR, actor.agent.vendor) ? AGENT_NAMES_BY_VENDOR[actor.agent.vendor]! : actor.agent.model
    case 'poulpe':
      return 'Poulpe'
  }
}

/** Why words do not answer a prompt, as the model refuses them. */
export type WordsMisfit = 'choices_only' | 'required' | 'not_a_number' | 'not_an_integer'

/** A finite number as Rust reads one, but for its infinities and NaN: `3`, `-0.5`, `.5`, `2.`, `1e3`. */
const NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/
/** A whole number as Rust reads one: `3`, `-12`, `+7`. */
const INTEGER = /^[+-]?\d+$/
const I64 = { min: -(2n ** 63n), max: 2n ** 63n - 1n }

/**
 * Why `text`, an answer in words, does not answer `prompt`, as the model's
 * Prompt::read_words refuses it; null when it does. Blank words leave a prompt
 * that is not required unanswered. The mock refuses by it, and the card
 * sends nothing it refuses.
 */
export function wordsMisfit(prompt: Prompt, text: string): WordsMisfit | null {
  if (prompt.choices_only) return 'choices_only'
  const words = text.trim()
  if (!words) return prompt.required ? 'required' : null
  if (prompt.words === 'number') return NUMBER.test(words) && Number.isFinite(Number(words)) ? null : 'not_a_number'
  if (prompt.words === 'integer') return INTEGER.test(words) && BigInt(words) >= I64.min && BigInt(words) <= I64.max ? null : 'not_an_integer'
  return null
}

/** An issue's key as the model writes it: its project's key, and its number in three digits at least (`Poulpe-007`). */
export function issueKey(projectKey: string, number: number): string {
  return `${projectKey}-${String(number).padStart(3, '0')}`
}

/** A plan's statuses, in their order, as today's board names them (plans/mod.rs, PlanStatus::name). */
export const PLAN_STATUSES: readonly { slug: PlanStatus; name: string }[] = [
  { slug: 'backlog', name: 'Backlog' },
  { slug: 'planned', name: 'Planned' },
  { slug: 'in_progress', name: 'In Progress' },
  { slug: 'for_review', name: 'For Review' },
  { slug: 'completed', name: 'Completed' },
  { slug: 'cancelled', name: 'Cancelled' },
]

/** Current plans: In Progress, then Planned. */
export const CURRENT_PLAN_STATUSES: readonly PlanStatus[] = ['in_progress', 'planned']

/** The Plans tab lists live work first, then the finished groups. */
export const PLAN_STATUS_PAGE_ORDER: readonly PlanStatus[] = ['in_progress', 'for_review', 'planned', 'backlog', 'completed', 'cancelled']

export function planStatusName(status: PlanStatus): string {
  return PLAN_STATUSES.find((s) => s.slug === status)?.name ?? status
}
